import { ZERO_WEI } from '@kwenta/sdk/constants'
import { useCallback } from 'react'

import { setOpenModal } from 'state/app/reducer'
import { handleOneClickOperation } from 'state/futures/actions'
import {
	selectIsConditionalOrder,
	selectMarketAsset,
	selectTradeOrderType,
	selectTradePanelInputs,
	selectTradingMode,
} from 'state/futures/common/selectors'
import { selectKeeperDepositExceedsBal, selectNewTradeHasSlTp } from 'state/futures/selectors'
import {
	approveSmartMargin,
	refetchSmartMarginTradePreview,
	submitSmartMarginOrder,
} from 'state/futures/snxPerpsV2/actions'
import {
	selectSmartMarginAllowanceValid,
	selectSmartMarginKeeperDeposit,
	selectSmartMarginTradePreview,
	selectV2MarketInfo,
} from 'state/futures/snxPerpsV2/selectors'
import { TradingModes } from 'state/futures/types'
import { useAppDispatch, useAppSelector, usePollAction } from 'state/hooks'

import TradeConfirmationModal from './TradeConfirmationModal'

export default function TradeConfirmationModalSmartMargin() {
	const dispatch = useAppDispatch()

	const isConditionalOrder = useAppSelector(selectIsConditionalOrder)
	const marketInfo = useAppSelector(selectV2MarketInfo)
	const hasSlTp = useAppSelector(selectNewTradeHasSlTp)
	const keeperEthDeposit = useAppSelector(selectSmartMarginKeeperDeposit)
	const potentialTradeDetails = useAppSelector(selectSmartMarginTradePreview)
	const orderType = useAppSelector(selectTradeOrderType)
	const marketAsset = useAppSelector(selectMarketAsset)
	const { stopLossPrice, takeProfitPrice } = useAppSelector(selectTradePanelInputs)
	const allowanceValid = useAppSelector(selectSmartMarginAllowanceValid)
	const tradingMode = useAppSelector(selectTradingMode)
	const keeperDepositExceedsBal = useAppSelector(selectKeeperDepositExceedsBal)

	usePollAction('refresh_preview', refetchSmartMarginTradePreview, { intervalTime: 6000 })

	const onDismiss = useCallback(() => {
		dispatch(setOpenModal(null))
	}, [dispatch])

	const handleApproveSmartMargin = useCallback(async () => {
		dispatch(approveSmartMargin())
	}, [dispatch])

	const onSubmit = useCallback(() => {
		dispatch(submitSmartMarginOrder(true))
	}, [dispatch])

	const handleSubmit = useCallback(async () => {
		tradingMode === TradingModes.ONE_CLICK
			? dispatch(handleOneClickOperation(onSubmit))
			: onSubmit()
	}, [dispatch, tradingMode, onSubmit])

	return (
		<TradeConfirmationModal
			perpsVersion={2}
			onDismiss={onDismiss}
			orderType={orderType}
			marketAsset={marketAsset}
			onApproveAllowance={handleApproveSmartMargin}
			sltp={{
				stopLossPrice: stopLossPrice?.toString(),
				takeProfitPrice: takeProfitPrice?.toString(),
			}}
			tradePreview={{
				margin: potentialTradeDetails?.margin,
				priceImpact: potentialTradeDetails?.priceImpact,
				size: potentialTradeDetails?.newSize ?? ZERO_WEI,
				price: potentialTradeDetails?.fillPrice ?? ZERO_WEI,
				exceedsPriceProtection: potentialTradeDetails?.exceedsPriceProtection,
				sizeDelta: potentialTradeDetails?.sizeDelta ?? ZERO_WEI,
				liqPrice: potentialTradeDetails?.liqPrice ?? ZERO_WEI,
			}}
			onSubmitOrder={handleSubmit}
			allowanceValid={allowanceValid}
			keeperDepositExceedsBalance={keeperDepositExceedsBal}
			fees={{
				tradeFee: potentialTradeDetails?.fee ?? ZERO_WEI,
				executionFee: marketInfo?.keeperDeposit ?? ZERO_WEI,
				keeperFee: isConditionalOrder || hasSlTp ? keeperEthDeposit : undefined,
			}}
		/>
	)
}
